import React, {useState} from "react";
import {observer} from "mobx-react-lite";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography} from "@mui/material";
import {useStores} from "../../../stores";
import {AdminPanelSettings, ChevronRight} from "@mui/icons-material";
import {SupplierNormalizationState} from "./SupplierNormalization.classes";
import {RocketImage} from "../../../components/icons/images/RocketImage.tsx";

export const SupplierNormalizationWelcomeStep: React.FC = observer(() => {
    const {p, bagStore, supplierNormalizationStore, authStore, materializedApi} = useStores();
    const [showDialog, setShowDialog] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            supplierNormalizationStore.goToState(SupplierNormalizationState.RUNNING);
        } catch (err) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((err as any).cause !== 'API Error')
                throw err;
            setError((err as Error).message);
        } finally {
            setShowDialog(false);
        }
    }

    return <>
        <Grid
            container
            component="section"
            className="page-item step"
            alignItems="center"
            direction="column"
            spacing={4}
            sx={{mt: 10}}>
            <Grid component="section">
                <RocketImage/>
            </Grid>

            <Grid component="section">
                <Typography variant="body2" className="text-center">
                    Mithra’s AI standardizes and merges supplier data for a reliable vendor master.
                </Typography>
            </Grid>

            <Grid>
                <Button
                    sx={{mx: 1}}
                    variant="outlined"
                    color="primary"
                    endIcon={<ChevronRight/>}
                    onClick={() => {
                        try {
                            supplierNormalizationStore.goToState(SupplierNormalizationState.REVIEW)
                        } catch (err) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            if ((err as any).cause !== 'API Error')
                                throw err;
                            setError((err as Error).message);
                        }
                    }}>
                    {p.p.disableSupplierNormalizationAiModel
                        ? 'Next' // Not sure how the flow should be here, so we simply call it "Next"
                        : 'Open results'
                    }
                </Button>
                {!p.p.disableSupplierNormalizationAiModel &&
                    <Button
                        sx={{mx: 1}}
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            if (p.p.skipAiSupplierNormalizationModel) {
                                supplierNormalizationStore.goToState(SupplierNormalizationState.RUNNING);
                            } else {
                                setShowDialog(true)
                            }
                        }}>
                        Start model
                    </Button>
                }
            </Grid>
        </Grid>

        {error !== '' && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

        <Dialog open={showDialog} onClose={() => setShowDialog(false)}
                maxWidth={authStore.isMithraStaffGroup ? 'sm' : 'xs'}>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {supplierNormalizationStore.jobDelegate.jobRunning && "Can't run AI"}
                    {!supplierNormalizationStore.jobDelegate.jobRunning && 'Confirm AI run'}
                </DialogTitle>
                <DialogContent>
                    {supplierNormalizationStore.jobDelegate.jobRunning &&
                        "Last AI job is not finished yet. You can't start another job until last one is completed."}
                    {!supplierNormalizationStore.jobDelegate.jobRunning &&
                        "Are you sure you want to run AI on current dataset? You will not be able to do any more reviews while AI is running"}
                </DialogContent>
                <DialogActions>
                    {authStore.isMithraStaffGroup && <>
                        <Button
                            color="error"
                            onClick={() => materializedApi.http.post(`/bag/${bagStore.bagId}/devops_cleanup_supplier_normalization_ai_results/`)
                                .then(() => window.location.reload())}
                            startIcon={<AdminPanelSettings/>}>
                            Clear AI & Reviews
                        </Button>
                        <Button
                            color="warning"
                            onClick={() => materializedApi.http.post(`/bag/${bagStore.bagId}/devops_cleanup_supplier_normalization/`)
                                .then(() => window.location.reload())}
                            startIcon={<AdminPanelSettings/>}
                            style={{marginRight: '2em'}}>
                            Clear AI
                        </Button>
                    </>}
                    {supplierNormalizationStore.jobDelegate.jobRunning &&
                        <Button variant="contained" color="error" onClick={() => setShowDialog(false)}>OK</Button>}
                    {!supplierNormalizationStore.jobDelegate.jobRunning && <>
                        <Button onClick={() => setShowDialog(false)}>Cancel</Button>
                        <Button type="submit" variant="contained" color="primary">Run AI</Button>
                    </>}
                </DialogActions>
            </form>
        </Dialog>
    </>
})
